import classNames from 'classnames'
import { ComponentProps, useState } from 'react'
import { useDebounce } from 'react-use'
import { Button, Card } from '@bpinternal/ui-kit-next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const MONTHS = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString('en', { month: 'long' }))

const isMoreThanNow = (date: Date) => date.valueOf() > Date.now()

function increment(date: Date, reverse?: boolean) {
  const newDate = new Date(date)
  newDate.setMonth(newDate.getMonth() + (reverse ? -1 : 1))
  return newDate
}

type Props = {
  selectedMonth: Date
  debouncedOnChange?: (month: Date) => void
  onChange?: (month: Date) => void
} & Omit<ComponentProps<typeof Card>, 'onChange'>

export const UsageMonthSelector = ({ selectedMonth, onChange, debouncedOnChange, ...props }: Props) => {
  const [_selectedMonth, setSelectedMonth] = useState<Date>(selectedMonth)

  useDebounce(() => debouncedOnChange?.(_selectedMonth), 500, [_selectedMonth])

  function handleOnChange(date: Date) {
    if (isMoreThanNow(date)) {
      return
    }

    setSelectedMonth(new Date(date))
    onChange?.(date)
  }

  return (
    <Card {...props} className={classNames('flex items-center gap-1 rounded-sm p-1', props.className)}>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-8"
        data-analytics="usage/month-selector/previous"
        onClick={() => handleOnChange(increment(_selectedMonth, true))}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <span className="text-sm">
        {MONTHS[_selectedMonth.getMonth()]?.slice(0, 3)} - {_selectedMonth.getFullYear()}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-8"
        data-analytics="usage/month-selector/next"
        onClick={() => handleOnChange(increment(_selectedMonth))}
        disabled={isMoreThanNow(increment(_selectedMonth))}
      >
        <ChevronRight className="size-4" />
      </Button>
    </Card>
  )
}
